import { Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { requestPasswordReset } from "@/lib/auth/auth-api";

import { AuthScreen } from "./auth-screen";
import { AuthField, TextInput } from "./auth-fields";

function BackToLogin() {
  const { t } = useTranslation();
  return (
    <Link
      to="/login"
      className="mb-1.5 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" aria-hidden />
      {t("auth.reset.backToLogin")}
    </Link>
  );
}

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!emailValid) return;
    setStatus("sending");
    try {
      await requestPasswordReset(email.trim());
    } finally {
      // Always land on the same confirmation — the endpoint never reveals
      // whether the address exists (anti-enumeration).
      setStatus("sent");
    }
  }

  if (status === "sent") {
    return (
      <AuthScreen>
        <div className="text-center">
          <div className="mx-auto mb-3.5 grid size-12 place-items-center rounded-full bg-success/10">
            <Mail className="size-6 text-success" aria-hidden />
          </div>
          <h1 className="text-2xl font-extrabold">{t("auth.reset.sentTitle")}</h1>
          <p className="mt-1.5 text-[14.5px] text-muted-foreground">
            {t("auth.reset.sentBody")}
          </p>
          <div className="mt-5 flex justify-center">
            <BackToLogin />
          </div>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <BackToLogin />
      <h1 className="text-2xl font-extrabold">{t("auth.reset.requestTitle")}</h1>
      <p className="mt-1.5 text-[14.5px] text-muted-foreground">
        {t("auth.reset.requestBody")}
      </p>
      <form noValidate onSubmit={submit}>
        <AuthField label={t("auth.email")} htmlFor="reset-email">
          <TextInput
            id="reset-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={setEmail}
            placeholder={t("auth.reset.emailPlaceholder")}
          />
        </AuthField>
        <Button
          type="submit"
          className="mt-5 w-full"
          disabled={!emailValid || status === "sending"}
        >
          {status === "sending" && <Loader2 className="size-4 animate-spin" aria-hidden />}
          {t("auth.reset.sendLink")}
        </Button>
      </form>
    </AuthScreen>
  );
}
